import { useCallback, useEffect, useState } from 'react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import api from '../../services/api';
import BriefingProgress from './BriefingProgress';
import BriefingRunLog from './BriefingRunLog';
import BriefingTags from './BriefingTags';
import type { NewsBeatDetail, NewsBriefingDetail } from '../../types';

/**
 * Everything a run did, as a slideout over the settings that caused it.
 *
 * THE SPLIT THIS ENFORCES. A briefing has two audiences and they want opposite
 * things. The reader wants the product: a few stories, read instead of the
 * articles. The person BUILDING the beat wants the evidence: what was fetched,
 * what the tagger saw, what got dropped and by which rule. Stacking the second
 * on top of the first served neither — the briefing page read as a debug dump,
 * and the diagnosis sat a tab away from the only screen that can act on it.
 *
 * So the evidence lives here, over Setup, where the knobs are. Change a filter,
 * re-process, watch it happen, promote a tag — and the tag lands in the form
 * behind this panel without leaving it. That is the same argument the agent's
 * tune/comb split already makes: at this altitude a wrong story is evidence
 * about the settings, and the place to look at evidence is next to the cause.
 *
 * Reachable from the Briefings tab too, scoped to whichever run is selected —
 * otherwise a briefing opened tomorrow has no account of how it came to look
 * that way, which is the thing `jcseo/news/runlog.py` exists to prevent.
 */

const UNREVIEWED_BECAUSE: Record<string, string> = {
  MAX_PASSES:
    'The run used all its judging rounds before reaching these. Raise "judging ' +
    'rounds per run" below, or fetch fewer articles.',
  STALLED:
    'A whole round judged nothing, so the run stopped rather than repeat it. ' +
    'Usually the filters are refusing everything left.',
  CANCELLED: 'The run was stopped before these were judged.',
  ERROR: 'The run failed part-way. The stories it had already written are kept.',
  MAX_ITER: 'The run hit its iteration limit before reaching these.',
};

export default function RunInspector({
  beat,
  briefingId,
  open,
  runState,
  error,
  reloadKey,
  onClose,
  onBeatChanged,
  onReprocess,
  onContinue,
}: {
  beat: NewsBeatDetail;
  briefingId: number | null;
  open: boolean;
  /** What the beat's latest run is doing. NOT a boolean — see `stuck` below. */
  runState: 'idle' | 'queued' | 'running';
  /** The page's last action error, repeated here: this drawer covers the
   *  banner that would otherwise carry it, so a 409 landed on a hidden line. */
  error?: string | null;
  /** Bumped by the page when a run lands, so the panel re-reads. */
  reloadKey?: number;
  onClose: () => void;
  /** A tag promotion rewrote the beat — the Setup form behind this reconciles. */
  onBeatChanged: (next: NewsBeatDetail) => void;
  /** Wipe and re-judge everything. Draft only — it deletes. */
  onReprocess?: (briefingId: number) => void;
  /** Judge only what was never reached, keeping the rest. Always available. */
  onContinue?: (briefingId: number) => void;
}) {
  const [briefing, setBriefing] = useState<NewsBriefingDetail | null>(null);
  useEscapeKey(open, onClose);

  const load = useCallback(async () => {
    if (!briefingId) {
      setBriefing(null);
      return;
    }
    try {
      setBriefing(await api.getNewsBriefing(briefingId));
    } catch {
      setBriefing(null);
    }
  }, [briefingId]);

  // Only while open: this panel is closed most of the time and there is no
  // reason for a collapsed drawer to keep refetching a finished run.
  useEffect(() => {
    if (open) load();
  }, [open, load, reloadKey]);

  const rejected = briefing?.rejected ?? [];
  const unreviewed = briefing?.unreviewed ?? [];

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/30 transition-opacity ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden
      />
      {/* Wider than the chat pane: this one holds a per-article table and three
          columns of tags, and a 28rem drawer would make both unreadable. */}
      <aside
        role="dialog"
        aria-label="How this run went"
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-3xl bg-white dark:bg-gray-800 shadow-xl flex flex-col transition-transform duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="shrink-0 flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            How this run went
            {briefing && (
              <span className="ml-2 font-normal text-xs text-gray-500 dark:text-gray-400">
                {new Date(briefing.created_at).toLocaleString()}
                {briefing.articles_fetched > 0 &&
                  ` · ${briefing.articles_fetched} fetched`}
              </span>
            )}
          </span>
          <div className="flex items-center gap-2">
            {onReprocess && briefingId && (
              <button
                onClick={() => onReprocess(briefingId)}
                disabled={runState === 'running'}
                className="text-xs px-2 py-1 rounded-md bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50"
                title={
                  runState === 'queued'
                    ? "A run is queued. If its worker died this starts over; if it is genuinely in flight the server will say so."
                    : "Throw away this run's stories and judge the same articles again with the current settings. No new search."
                }
              >
                Re-judge from scratch
              </button>
            )}
            <button
              onClick={onClose}
              aria-label="Close"
              className="icon-btn text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
          {/* The page's error banner is UNDER this drawer, so an action that
              409s here used to fail into a line nobody could see. */}
          {error && (
            <p
              role="alert"
              className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300"
            >
              {error}
            </p>
          )}

          {/* QUEUED IS NOT RUNNING, and this panel is where you find that out.
              A worker that dies before stamping `started_at` leaves the row
              QUEUED forever; the buttons above stay live because the server
              already frees a provably dead job (news/reprocess.blocking_reason)
              and 409s when the run is genuinely in flight. */}
          {runState === 'queued' && (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
              A run for this beat is <strong>queued</strong> and has not reported
              starting. If its worker is gone, the buttons here start over
              anyway; if it really is in flight, the server will say so and
              nothing is lost.
            </p>
          )}

          {!briefingId ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              This beat has not run yet. Use <strong>Fetch fresh articles</strong>{' '}
              and this panel will fill in as it goes.
            </p>
          ) : (
            <>
              <BriefingProgress briefingId={briefingId} running={runState !== 'idle'} />

              <BriefingTags
                beat={beat}
                briefingId={briefingId}
                reloadKey={reloadKey}
                onChanged={onBeatChanged}
                onReprocess={onReprocess}
              />

              <BriefingRunLog briefingId={briefingId} />

              {rejected.length > 0 && (
                <details
                  className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2"
                  open
                >
                  <summary className="cursor-pointer text-sm font-medium text-gray-900 dark:text-gray-100">
                    Dropped by filters ({rejected.length})
                  </summary>
                  <p className="mt-1 mb-2 text-xs text-gray-500 dark:text-gray-400">
                    The reason each one gives is the agent's own, recorded when it
                    decided - so this is the evidence for whether a filter is set
                    right, not a guess after the fact.
                  </p>
                  <ul className="space-y-1.5">
                    {rejected.map((row) => (
                      <li key={row.article.id} className="text-sm">
                        <a
                          href={row.article.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-indigo-600 dark:text-indigo-400 hover:underline"
                        >
                          {row.article.title}
                        </a>
                        <span className="text-gray-500 dark:text-gray-400">
                          {row.rejected_by_attribute && (
                            <>
                              {' - '}
                              <code>{row.rejected_by_attribute}</code>:{' '}
                            </>
                          )}
                          {row.reject_reason}
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {unreviewed.length > 0 && (
                <details
                  className="rounded-lg border border-amber-300 dark:border-amber-700 px-3 py-2"
                  open
                >
                  <summary className="cursor-pointer text-sm font-medium text-amber-800 dark:text-amber-300">
                    Never judged ({unreviewed.length})
                  </summary>
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                    {UNREVIEWED_BECAUSE[briefing?.ended_reason ?? ''] ??
                      'The run ended before these were judged.'}
                  </p>
                  {/* The obvious next move, and not the same as Re-process:
                      this keeps every story already written and picks up only
                      what is left. Nothing is deleted, so it needs no draft. */}
                  {onContinue && (
                    <p className="mt-1.5 mb-2">
                      <button
                        onClick={() => onContinue(briefingId)}
                        disabled={runState === 'running'}
                        className="text-xs px-2 py-1 rounded-md bg-amber-600 text-white hover:bg-amber-500 disabled:opacity-50"
                        title={
                          runState === 'queued'
                            ? 'A run is queued. If its worker died this picks these up anyway; if it is genuinely in flight the server will say so.'
                            : 'Judge these now, keeping the stories this run already wrote'
                        }
                      >
                        Judge these {unreviewed.length} now
                      </button>
                    </p>
                  )}
                  <ul className="space-y-1">
                    {unreviewed.map((a) => (
                      <li key={a.id} className="text-sm">
                        <a
                          href={a.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-indigo-600 dark:text-indigo-400 hover:underline"
                        >
                          {a.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
}
